"use client";

import React, { useRef } from "react";
import type { ZipCodeConnectedProps } from "dds/molecules/zip-code/zip-code-connected.client";
import { useLineupMarketTarget } from "dds/organisms/channel-lineup/lineup-market.client";
import { STORAGE_LOCALS_ELIGIBLE } from "dds/services/zip/zip-store";
import type { RawStation } from "dds/services/zip/local-rsn-channels";
import type { LocalStationRules } from "lib/channel-lineup/local-stations";
import { ZipCodeLazy } from "../v3-zip-code/zip-code-lazy.client";
import { fetchLineupLocals, lineupMarketFor } from "./load-lineup-market";

/* RSNs only from the session: beam is never a locals source here. */
const STREAM_RSNS_ONLY = {
  stream: true,
  local: false
};
const SATELLITE_RSNS_ONLY = {
  satellite: true,
  local: false
};

/* `local: false` makes the shared session store "no locals" for every market, and other pages' plan
   cards read that key. Restate it from the route's answer; no answer clears it (unknown = eligible). */
function storeLocalsEligibility(stations: readonly RawStation[] | null): void {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }
    if (stations === null) {
      window.sessionStorage.removeItem(STORAGE_LOCALS_ELIGIBLE);
    } else {
      window.sessionStorage.setItem(STORAGE_LOCALS_ELIGIBLE, String(stations.length > 0));
    }
  } catch {
    // Storage blocked: nothing reads it on this page.
  }
}

/* A malformed beam or station row: dev-only, `[ChannelLineup]`-prefixed, as the grid's own warns are. */
function warnRowsFailed(error: unknown): void {
  if (process.env.NODE_ENV !== "production") {
    console.warn("[ChannelLineup] market rows failed to build", error);
  }
}

/* The key is read only by satellite plan cards, so only a satellite lineup writes the route's answer;
   an OTT lineup just clears beam's blanket "false" (unknown = eligible), and only until a satellite
   lineup has asked. Among writers, the most recently asked market wins. */
let latestEligibilityRequest = 0;
let satelliteAsked = false;

/** Test-only: forget which lineups asked. */
export function __resetLocalsEligibilityState(): void {
  latestEligibilityRequest = 0;
  satelliteAsked = false;
}

export interface LineupZipControlProps extends Omit<ZipCodeConnectedProps, "callbacks" | "localRsnApiOptions"> {
  /** The lineup's authored package codes, built on the server. Absent, no local rows. */
  localStationRules?: LocalStationRules;
}

/**
 * The lineup's ZIP Code. The shared ZIP session supplies the DMA and beam's in-market RSNs; locals
 * come from `/api/local-channels`, placed by billing code. The server never waits on either.
 */
export function LineupZipControl({ localStationRules, localChannelsBasePath, ...props }: LineupZipControlProps) {
  const target = useLineupMarketTarget();
  // A slower answer for an earlier ZIP must not overwrite the current one.
  const latest = useRef(0);
  const inFlight = useRef<AbortController | undefined>(undefined);
  const platform = target?.platform === "satellite" ? "satellite" : "ott";

  const callbacks: ZipCodeConnectedProps["callbacks"] = {
    onLocalRsnChnlsConfirmed: (channels, _dma, nielsenDma) => {
      latest.current += 1;
      const request = latest.current;
      const writesEligibility = platform === "satellite" || !satelliteAsked;
      if (platform === "satellite") {
        satelliteAsked = true;
      }
      if (writesEligibility) {
        latestEligibilityRequest += 1;
      }
      const eligibilityRequest = latestEligibilityRequest;
      inFlight.current?.abort();
      const controller = typeof AbortController === "undefined" ? undefined : new AbortController();
      inFlight.current = controller;
      const publish = (stations: readonly RawStation[] | null) => {
        if (request !== latest.current) {
          return;
        }
        target?.publish(lineupMarketFor({
          stations,
          rsnChannels: channels?.rsnChannels,
          rules: localStationRules,
          columnCodes: target.columnCodes
        }));
      };
      // RSNs first, so they never wait on the locals route; locals join when they arrive.
      try {
        publish(null);
      } catch (error) {
        warnRowsFailed(error);
        // Never leave the previous market's rows on screen: clear to an empty market.
        if (request === latest.current) {
          target?.publish(lineupMarketFor({
            stations: null,
            rsnChannels: [],
            rules: localStationRules,
            columnCodes: target.columnCodes
          }));
        }
      }
      fetchLineupLocals(nielsenDma, platform, localChannelsBasePath, controller?.signal)
        .then(stations => {
          if (request !== latest.current) {
            return;
          }
          // Eligibility depends only on the route's answer, so it is written before a publish that can throw.
          if (writesEligibility && eligibilityRequest === latestEligibilityRequest) {
            storeLocalsEligibility(platform === "satellite" ? stations : null);
          }
          // null and [] draw the RSN-only market already published; skip the identical rebuild.
          if ((stations?.length ?? 0) > 0) {
            publish(stations);
          }
        })
        // `fetchLineupLocals` never rejects: this sees a row that fails to build (beam RSN or station)
        // or a throw from the provider's publish.
        .catch(warnRowsFailed);
    }
  };

  return (
    <ZipCodeLazy
      {...props}
      callbacks={callbacks}
      localChannelsBasePath={localChannelsBasePath}
      // Stream RSNs by Nielsen DMA, satellite by DTV DMA.
      localRsnApiOptions={platform === "satellite" ? SATELLITE_RSNS_ONLY : STREAM_RSNS_ONLY}
    />
  );
}

export default LineupZipControl;
